import { VStack } from '@chakra-ui/react';
import type { Dispatch, RefObject } from 'react';
import type {
  SelectOption,
  EditorProps,
  EmailApiHandle,
} from '@kula-ai/components';
import { EmailBlockSection } from '../../../components/EmailBlockSection';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import type {
  InterviewCommunicationAction,
  InterviewCommunicationFormState,
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../../../InterviewActivity/communication/types';
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/copy';
import {
  EmailSentBadge,
  type EmailSentBadgeProps,
} from '../../editMode/EmailSentBadge';

interface CandidatePaneProps {
  state: InterviewCommunicationFormState;
  dispatch: Dispatch<InterviewCommunicationAction>;
  fieldErrors: InterviewCommunicationValidationErrors;
  /** Bumped per submit click — surfaces each Email's errors on submit. */
  submitAttempt?: number;
  /** Imperative handle of the calendar-invite Email — `validate()` calls
   *  `focusFirstError()` on it to scroll to the bad field/variable. */
  calendarApiRef?: RefObject<EmailApiHandle | null>;
  /** Imperative handle of the email Email. */
  emailApiRef?: RefObject<EmailApiHandle | null>;
  /** Calendar-invite template options — `candidate_interview_notice`. */
  calendarTemplateOptions: SelectOption[];
  /** Email template options — `candidate_interview_confirmation`. */
  emailTemplateOptions: SelectOption[];
  fromOptions: SelectOption[];
  /** `true` while the delegatable-senders fetch is still resolving —
   *  forwarded to the From Select so it renders a spinner instead of
   *  an empty dropdown. */
  isFromOptionsLoading?: boolean;
  ccBccOptions: SelectOption[];
  calendarVariables: InterviewVariablesByCategory;
  emailVariables: InterviewVariablesByCategory;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
  sentEmailBadge?: EmailSentBadgeProps;
  calendarPreviewProps?: Pick<
    EditorProps,
    'previewHtml' | 'onPreviewClick' | 'onPreviewDismiss'
  >;
  emailPreviewProps?: Pick<
    EditorProps,
    'previewHtml' | 'onPreviewClick' | 'onPreviewDismiss'
  >;
}

export const CandidatePane = ({
  state,
  dispatch,
  fieldErrors,
  submitAttempt,
  calendarApiRef,
  emailApiRef,
  calendarTemplateOptions,
  emailTemplateOptions,
  fromOptions,
  isFromOptionsLoading = false,
  ccBccOptions,
  calendarVariables,
  emailVariables,
  uploadInlineImage,
  sentEmailBadge,
  calendarPreviewProps,
  emailPreviewProps,
}: CandidatePaneProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.communication;

  return (
    <VStack align="stretch" spacing="4" flex="1" minW={0}>
      <EmailBlockSection
        title={copy.candidate.calendarTitle}
        sectionId="schedule-comm-candidate-calendar"
      >
        <TemplatedEmail
          className="w-full"
          showFrom={false}
          showTo={false}
          showSubject={false}
          enableCcBcc={false}
          showTemplate
          showEditor
          editorVariant="calendarInvite"
          submitAttempt={submitAttempt}
          apiRef={calendarApiRef}
          variables={calendarVariables}
          templateOptions={calendarTemplateOptions}
          applyTemplateBodyToEditor
          value={state.candidateCalendar}
          onChange={(value) =>
            dispatch({ type: 'SET_CANDIDATE_CALENDAR', payload: value })
          }
          errors={fieldErrors.candidateCalendar}
          placeholders={{ template: copy.placeholders.template }}
          uploadInlineImage={uploadInlineImage}
          editorProps={calendarPreviewProps}
        />
      </EmailBlockSection>

      <EmailBlockSection
        title={copy.candidate.emailTitle}
        sectionId="schedule-comm-candidate-email"
        showSwitch
        switchChecked={state.candidateEmailEnabled}
        onSwitchChange={(checked) =>
          dispatch({ type: 'SET_CANDIDATE_EMAIL_ENABLED', payload: checked })
        }
        suffix={
          sentEmailBadge ? (
            <EmailSentBadge {...sentEmailBadge} showTooltip={false} />
          ) : undefined
        }
      >
        {state.candidateEmailEnabled ? (
          <TemplatedEmail
            className="w-full"
            showFrom
            showTo={false}
            showSubject
            enableCcBcc
            showTemplate
            showEditor
            // "Include calendar invite (.ics file)" now lives in the editor
            // toolbar (kula `Email` `showIcsCheckbox`); its state rides on
            // `value.ics`, which we keep mirrored to `candidateIcsEnabled`.
            showIcsCheckbox
            editorVariant="richEmail"
            submitAttempt={submitAttempt}
            apiRef={emailApiRef}
            variables={emailVariables}
            attachments={state.candidateEmailAttachments}
            onAttachmentsChange={(items: InterviewEditorAttachmentItem[]) =>
              dispatch({
                type: 'SET_CANDIDATE_EMAIL_ATTACHMENTS',
                payload: items,
              })
            }
            templateOptions={emailTemplateOptions}
            fromOptions={fromOptions}
            fromSelectProps={{ loading: isFromOptionsLoading }}
            ccBccOptions={ccBccOptions}
            applyTemplateBodyToEditor
            value={{ ...state.candidateEmail, ics: state.candidateIcsEnabled }}
            onChange={(value) => {
              dispatch({ type: 'SET_CANDIDATE_EMAIL', payload: value });
              if (value.ics !== state.candidateIcsEnabled) {
                dispatch({
                  type: 'SET_CANDIDATE_ICS_ENABLED',
                  payload: value.ics,
                });
              }
            }}
            errors={fieldErrors.candidateEmail}
            placeholders={{
              from: copy.placeholders.from,
              subject: copy.placeholders.subject,
              template: copy.placeholders.template,
            }}
            uploadInlineImage={uploadInlineImage}
            editorProps={emailPreviewProps}
          />
        ) : null}
      </EmailBlockSection>
    </VStack>
  );
};
